import { redirect } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/server";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, HardHat, Plus, Users, Star, FileText, CheckCircle2, Phone, UserPlus, BarChart3, RotateCcw } from "lucide-react";
import { SignOutButton } from "@/components/sign-out-button";
import { BottomNavWrapper } from "@/components/bottom-nav-wrapper";
import { ProfileChecklist, getContractorChecklist, getWorkerChecklist } from "@/components/profile-checklist";
import { WelcomeCard } from "@/components/welcome-card";
import { OnboardingSurvey } from "@/components/onboarding-survey";

export default async function DashboardPage() {
  const supabase = await createClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    redirect("/auth/login");
  }

  const { data: profile } = await supabase
    .from("profiles")
    .select("id, type, name, phone, email, suburb, avatar_url, onboarding_completed")
    .eq("id", user.id)
    .single();

  const isContractor = profile?.type === "contractor";

  // Redirect users who haven't completed onboarding to their signup flow
  if (profile && !profile.onboarding_completed) {
    const onboardingPath = isContractor
      ? "/contractor/signup"
      : "/worker/signup";
    redirect(onboardingPath);
  }

  // Fetch stats
  let jobCount = 0;
  let matchCount = 0;
  let pendingRatings: { matchId: string; otherName: string }[] = [];

  let companyData: { id: string; name: string | null; abn: string | null; address: string | null; industry: string | null; website: string | null; logo_url: string | null } | null = null;
  let hasContacts = false;
  let hiredCount = 0;

  if (isContractor) {
    const { data: company } = await supabase
      .from("companies")
      .select("id, name, abn, address, industry, website, logo_url")
      .eq("profile_id", user.id)
      .single();

    companyData = company;

    if (company) {
      const { count } = await supabase
        .from("jobs")
        .select("*", { count: "exact", head: true })
        .eq("company_id", company.id)
        .eq("status", "active");
      jobCount = count || 0;

      const { count: mCount } = await supabase
        .from("matches")
        .select("*, jobs!inner(company_id)", { count: "exact", head: true })
        .eq("jobs.company_id", company.id)
        .in("status", ["applied", "suggested"]);
      matchCount = mCount || 0;

      const { count: contactCount } = await supabase
        .from("company_contacts")
        .select("*", { count: "exact", head: true })
        .eq("company_id", company.id);
      hasContacts = (contactCount || 0) > 0;

      const { count: hCount } = await supabase
        .from("matches")
        .select("*, jobs!inner(company_id)", { count: "exact", head: true })
        .eq("jobs.company_id", company.id)
        .eq("status", "hired");
      hiredCount = hCount || 0;
    }
  }

  // Worker profile data for checklist
  let workerProfileData: { trades: string[] | null; experience_years: number | null; white_card_number: string | null } | null = null;
  if (!isContractor) {
    const { data: wp } = await supabase
      .from("worker_profiles")
      .select("trades, experience_years, white_card_number")
      .eq("profile_id", user.id)
      .single();
    workerProfileData = wp;
  }

  // Fetch hired matches that haven't been rated yet
  const { data: hiredMatches } = await supabase
    .from("matches")
    .select("id, worker_id, jobs(title)")
    .eq("status", "hired");

  if (hiredMatches && hiredMatches.length > 0) {
    const matchIds = hiredMatches.map((m) => m.id);
    const workerIds = hiredMatches.map((m) => m.worker_id).filter(Boolean);

    // Batch fetch all ratings and profiles in parallel
    const [{ data: existingRatings }, { data: profiles }] = await Promise.all([
      supabase
        .from("ratings")
        .select("id, match_id")
        .in("match_id", matchIds)
        .eq("from_id", user.id),
      workerIds.length > 0 && isContractor
        ? supabase.from("profiles").select("id, name").in("id", workerIds)
        : Promise.resolve({ data: [] }),
    ]);

    // Build lookup maps
    const ratedMatchIds = new Set(existingRatings?.map((r) => r.match_id) || []);
    const profileMap = new Map(profiles?.map((p) => [p.id, p.name]) || []);

    // Build pending ratings list
    for (const match of hiredMatches) {
      if (!ratedMatchIds.has(match.id)) {
        const otherId = isContractor ? match.worker_id : null;
        const name = otherId && profileMap.has(otherId) ? profileMap.get(otherId)! : "your hire";
        pendingRatings.push({ matchId: match.id, otherName: name });
      }
    }
  }

  // Fetch hired jobs for workers
  let workerHires: { matchId: string; jobTitle: string; companyName: string | null; hiredAt: string | null }[] = [];
  if (!isContractor) {
    const { data: hires } = await supabase
      .from("matches")
      .select("id, hired_at, jobs(title, companies(name))")
      .eq("worker_id", user.id)
      .eq("status", "hired")
      .order("hired_at", { ascending: false })
      .limit(5);

    if (hires) {
      workerHires = hires.map((h: any) => ({
        matchId: h.id,
        jobTitle: h.jobs?.title || "Job",
        companyName: h.jobs?.companies?.name || null,
        hiredAt: h.hired_at,
      }));
    }
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-6">
        <h1 className="text-2xl font-bold">
          G&apos;day{profile?.name ? `, ${profile.name}` : ""}!
        </h1>
        <p className="mt-1 text-muted-foreground">
          {isContractor
            ? "Ready to find your next worker?"
            : "Let's get you on a job."}
        </p>

        <div className="mt-6 space-y-3">
          {/* Pending ratings prompt */}
          {pendingRatings.length > 0 && (
            <Card className="border-yellow-500/30 bg-yellow-500/5">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Star className="h-6 w-6 text-yellow-500" />
                  Rate your experience
                </CardTitle>
                <CardDescription className="text-base">
                  Your feedback helps build trust on RateRight
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {pendingRatings.slice(0, 3).map((pr) => (
                  <div key={pr.matchId} className="flex gap-2">
                    <Button asChild variant="outline" className="flex-1 h-12 text-base justify-start">
                      <Link href={`/rate/${pr.matchId}`}>
                        <Star className="h-4 w-4 mr-2 text-yellow-500" />
                        Rate {pr.otherName}
                      </Link>
                    </Button>
                  </div>
                ))}
                {pendingRatings.length > 3 && (
                  <p className="text-sm text-muted-foreground text-center">
                    +{pendingRatings.length - 3} more to rate
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {isContractor ? (
            <>
              {/* Welcome card for new users */}
              <WelcomeCard
                userType="contractor"
                userName={profile?.name || undefined}
                hasPostedJob={jobCount > 0}
              />

              {/* Onboarding survey — shows once on first visit */}
              <OnboardingSurvey
                userType="contractor"
                userName={profile?.name || undefined}
              />

              {/* Profile completeness checklist */}
              <ProfileChecklist
                items={getContractorChecklist({
                  hasName: !!profile?.name,
                  hasPhone: !!profile?.phone,
                  hasEmail: !!profile?.email,
                  hasCompanyName: !!companyData?.name,
                  hasAbn: !!companyData?.abn,
                  hasAddress: !!companyData?.address,
                  hasIndustry: !!companyData?.industry,
                  hasWebsite: !!companyData?.website,
                  hasLogo: !!companyData?.logo_url,
                  hasPostedJob: jobCount > 0,
                  hasContacts,
                })}
                userType="contractor"
              />

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardContent className="flex flex-col items-center p-4">
                    <span className="text-3xl font-bold">{jobCount}</span>
                    <span className="text-xs text-muted-foreground mt-1">Active Jobs</span>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="flex flex-col items-center p-4">
                    <span className="text-3xl font-bold">{matchCount}</span>
                    <span className="text-xs text-muted-foreground mt-1">New Matches</span>
                  </CardContent>
                </Card>
              </div>

              {/* Post a Job CTA */}
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Plus className="h-6 w-6" />
                    Post a Job
                  </CardTitle>
                  <CardDescription className="text-base">
                    AI writes the post. You just tap.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full h-16 text-lg font-semibold">
                    <Link href="/contractor/post-job">Post a Job</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* View Matches */}
              {matchCount > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-6 w-6" />
                      {matchCount} Worker{matchCount > 1 ? "s" : ""} Matched
                    </CardTitle>
                    <CardDescription className="text-base">
                      Workers have applied to your jobs
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="outline" className="w-full h-12 text-base font-semibold">
                      <Link href="/contractor/matches">View Matches</Link>
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Past Hires — rehire workers */}
              {hiredCount > 0 && (
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <RotateCcw className="h-6 w-6 text-green-600" />
                      Past Hires
                    </CardTitle>
                    <CardDescription className="text-base">
                      {hiredCount} worker{hiredCount > 1 ? "s" : ""} hired — tap to rehire or contact
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="outline" className="w-full h-12 text-base font-semibold">
                      <Link href="/contractor/matches?tab=hired">View Past Hires</Link>
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Company profile */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building2 className="h-6 w-6" />
                    Company Profile
                  </CardTitle>
                  <CardDescription className="text-base">
                    Manage your company details
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full h-12 text-base">
                    <Link href="/contractor/profile/edit">Edit Profile</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Team Contacts */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <UserPlus className="h-6 w-6" />
                    Team Contacts
                  </CardTitle>
                  <CardDescription className="text-base">
                    Add site supers, admin, or accounts staff that workers can contact
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full h-12 text-base">
                    <Link href="/contractor/contacts">Manage Contacts</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Analytics */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BarChart3 className="h-6 w-6" />
                    Analytics
                  </CardTitle>
                  <CardDescription className="text-base">
                    Platform stats, signups, and hire funnel
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full h-12 text-base">
                    <Link href="/contractor/analytics">View Analytics</Link>
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Welcome card for new workers */}
              <WelcomeCard
                userType="worker"
                userName={profile?.name || undefined}
              />

              {/* Onboarding survey — shows once on first visit */}
              <OnboardingSurvey
                userType="worker"
                userName={profile?.name || undefined}
              />

              {/* Profile completeness checklist */}
              <ProfileChecklist
                items={getWorkerChecklist({
                  hasName: !!profile?.name,
                  hasPhone: !!profile?.phone,
                  hasEmail: !!profile?.email,
                  hasTrades: !!(workerProfileData?.trades && workerProfileData.trades.length > 0),
                  hasExperience: !!workerProfileData?.experience_years,
                  hasSuburb: !!profile?.suburb,
                  hasWhiteCard: !!workerProfileData?.white_card_number,
                  hasAvatar: !!profile?.avatar_url,
                })}
                userType="worker"
              />

              {/* Browse Jobs CTA */}
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <HardHat className="h-6 w-6" />
                    Find Work
                  </CardTitle>
                  <CardDescription className="text-base">
                    Browse available construction jobs near you
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild className="w-full h-16 text-lg font-semibold">
                    <Link href="/worker/jobs">Browse Jobs</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Your Hired Jobs */}
              {workerHires.length > 0 && (
                <Card className="border-green-500/30 bg-green-500/5">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                      Your Hired Jobs
                    </CardTitle>
                    <CardDescription className="text-base">
                      Tap to view contact details
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {workerHires.map((hire) => (
                      <Button key={hire.matchId} asChild variant="outline" className="w-full h-14 text-base justify-start">
                        <Link href={`/worker/hired/${hire.matchId}`}>
                          <Phone className="h-4 w-4 mr-3 text-green-600" />
                          <div className="text-left">
                            <span className="font-semibold">{hire.jobTitle}</span>
                            {hire.companyName && (
                              <span className="text-sm text-muted-foreground ml-2">at {hire.companyName}</span>
                            )}
                          </div>
                        </Link>
                      </Button>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Onboarding Pack CTA */}
              <Card className="border-blue-500/30 bg-blue-500/5">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                    Onboarding Pack
                  </CardTitle>
                  <CardDescription className="text-base">
                    Fill once, send to every contractor. Cuts admin from 30 min to 5.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full h-12 text-base font-semibold">
                    <Link href="/worker/onboarding-pack">Create Your Pack</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Profile */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <HardHat className="h-6 w-6" />
                    Your Profile
                  </CardTitle>
                  <CardDescription className="text-base">
                    {profile?.onboarding_completed
                      ? "View and manage your profile"
                      : "A strong profile gets you more matches"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="outline" className="w-full h-12 text-base">
                    <Link href={profile?.onboarding_completed ? "/worker/profile" : "/worker/profile/build"}>
                      {profile?.onboarding_completed ? "View Profile" : "Build Profile"}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>

      <BottomNavWrapper userType={profile?.type || "worker"} />
    </div>
  );
}
